import React from "react";

import Tooltip from "@/common/components/tooltip/Tooltip";
import LabelPalette, {
  getBadgeProps,
  DomainMap,
  PaletteEntry,
} from "@/common/constants/LabelPalette";
import { cn } from "@/common/utils/utils";
import { Flex, Text } from "@/compat/radix-themes";

/** Shared props used by all your badge wrappers */
export type UniversalBadgeProps = {
  /** domain map to lookup styles (e.g. LabelPalette.TEST_RESULT) */
  domain: DomainMap;
  /** value/key returned by server (case-insensitive) */
  value?: string | null;
  /** choose rendering style (default = lite) */
  variant?: "solid" | "lite" | "textOnly";
  /** legacy compat - treat as textOnly */
  isTextOnly?: boolean;
  /** show icon even in textOnly mode */
  showIcon?: boolean;
  /** size for padding */
  size?: "xs" | "sm" | "md";
  /** add subtle ring for lite variant (default false) */
  ringLite?: boolean;
  className?: string;
  /** force uppercase label text */
  isUpperCase?: boolean;
  /** Optional label prefix text e.g. "Type:" */
  label?: string;
  labelClassName?: string;
  /** internal: use inline Text for text-only option (Radix Text) */
  useTextComponent?: boolean;
  /** show tooltip on hover (true = auto-use status text, string = custom text) */
  tooltip?: boolean | string;
  textSize?:
    | "1"
    | "2"
    | "3"
    | "4"
    | "5"
    | "6"
    | "7"
    | "8"
    | "9"
    | Array<"1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9">;
};

/** padding by size */
const sizePadding: Record<string, string> = {
  xxs: "px-2 py-0.5 text-xxs",
  xs: "px-2 py-0.5 text-[10px]",
  sm: "px-2 py-1 typo-body",
  md: "px-3 py-1.5 typo-body",
  xl: "px-4 py-2.5 text-xl",
};

/**
 * StatusBadge
 * - minimal, single component to render any palette-driven badge
 * - Prevents unwanted flex-grow inside table cells by default
 */
export const StatusBadge: React.FC<UniversalBadgeProps> = ({
  domain,
  value,
  variant = "lite",
  isTextOnly = false,
  showIcon = true,
  size = "xs",
  ringLite = false,
  className = "",
  isUpperCase = false,
  label,
  labelClassName = "",
  useTextComponent = true,
  tooltip,
  textSize = "2" as const,
}) => {
  const p: PaletteEntry = getBadgeProps(domain, value ?? undefined);
  const padding = sizePadding[size] ?? sizePadding.sm;
  const tooltipText =
    tooltip === true
      ? p.text
      : typeof tooltip === "string"
        ? tooltip
        : undefined;

  const wrapWithTooltip = (node: React.ReactElement) =>
    tooltipText ? <Tooltip content={tooltipText}>{node}</Tooltip> : node;

  // choose classes from palette entry
  const chosenClass =
    isTextOnly || variant === "textOnly"
      ? p.textClass
      : variant === "solid"
        ? p.solidClass
        : p.liteClass;
  const ring =
    variant === "lite" && ringLite ? " ring-1 ring-inset ring-gray-100" : "";

  // text-only fallback (uses Radix Text optionally)
  if (isTextOnly || variant === "textOnly") {
    // Build the content with optional icon + text, preserving accessibility.
    const content = (
      <span className={cn("inline-flex items-center gap-2", labelClassName)}>
        {showIcon && (
          <span aria-hidden className="inline-flex items-center">
            {p.icon}
          </span>
        )}
        <span>
          {label
            ? `${label} ${isUpperCase ? p.text.toUpperCase() : p.text}`
            : isUpperCase
              ? p.text.toUpperCase()
              : p.text}
        </span>
      </span>
    );

    if (useTextComponent) {
      return wrapWithTooltip(
        // Use the palette textClass so colors still apply, and allow additional className
        <Text size={textSize as any} className={cn(p.textClass, className)}>
          {content}
        </Text>,
      );
    }

    return wrapWithTooltip(
      <span
        aria-label={p.text}
        className={cn(
          p.textClass,
          padding,
          "font-regular inline-flex items-center",
          className,
        )}
      >
        {content}
      </span>,
    );
  }

  // non-textOnly variants: badge with background / lite / solid
  return wrapWithTooltip(
    <Flex
      gap="1"
      px="2"
      justify="center"
      align="center"
      className={cn(
        "inline-flex flex-none w-auto max-w-max rounded-md items-center",
        padding,
        chosenClass,
        ring,
        className,
      )}
    >
      {showIcon && <span className="inline-flex items-center">{p.icon}</span>}
      <span
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap font-medium",
          labelClassName,
        )}
      >
        <span>{isUpperCase ? p.text.toUpperCase() : p.text}</span>
        {label && <span>{label}</span>}
      </span>
    </Flex>,
  );
};

export default StatusBadge;

export function makeBadge(domain: DomainMap) {
  return function BadgeWrapper({
    status,
    label,
    labelClassName,
    variant,
    showIcon,
    size,
    ringLite,
    className,
    isUpperCase,
    tooltip,
    // allow both `status` and `type` and `value` prop names for flexibility
    type,
    value,
    textSize,
  }: {
    status?: string | null;
    type?: string | null;
    value?: string | null;
    label?: string;
    labelClassName?: string;
    variant?: "solid" | "lite" | "textOnly";
    showIcon?: boolean;
    size?: "xs" | "sm" | "md";
    ringLite?: boolean;
    className?: string;
    isUpperCase?: boolean;
    tooltip?: boolean | string;
    textSize?:
      | "1"
      | "2"
      | "3"
      | "4"
      | "5"
      | "6"
      | "7"
      | "8"
      | "9"
      | Array<"1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9">;
  }) {
    const val = status ?? type ?? value ?? undefined;
    return (
      <StatusBadge
        domain={domain}
        value={val}
        label={label}
        labelClassName={labelClassName}
        variant={variant}
        showIcon={showIcon}
        size={size}
        ringLite={ringLite}
        className={className}
        isUpperCase={isUpperCase}
        tooltip={tooltip}
        textSize={textSize}
      />
    );
  };
}

export const TestResultBadge = makeBadge(LabelPalette.TEST_RESULT);
export const TestTypeBadge = makeBadge(LabelPalette.TEST_TYPE);
export const TestCaseStatusBadge = makeBadge(LabelPalette.TESTCASE_STATUS);
export const DefectStatusBadge = makeBadge(LabelPalette.DEFECT_STATUS);
export const DefectTypeBadge = makeBadge(LabelPalette.DEFECT_TYPE);
export const PriorityBadge = makeBadge(LabelPalette.PRIORITY);
export const SeverityBadge = makeBadge(LabelPalette.SEVERITY);
export const ExecutionStatusBadge = makeBadge(LabelPalette.EXECUTION_STATUS);
export const CycleStatusBadge = makeBadge(LabelPalette.CYCLE_STATUS);
// Requirement status values (READY/DRAFT/…) reuse the test-case status palette
// so every link dialog renders a consistent status badge.
export const RequirementStatusBadge = makeBadge(LabelPalette.TESTCASE_STATUS);
